import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Circle, ListTodo, StickyNote, Plus, Loader2, ChevronDown } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const NoahTasks = ({ customerId, campaignId, compact = false }) => {
  const [tasks, setTasks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [open, setOpen] = useState(!compact);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!customerId) return;
    try {
      const [t, a] = await Promise.all([
        axios.get(`${API}/tasks`, { params: { customer_id: customerId } }),
        axios.get(`${API}/noah/actions`, { params: { customer_id: customerId, limit: 30 } }),
      ]);
      setTasks(t.data || []);
      setNotes((a.data || []).filter(x => x.action === 'note' && x.ok));
    } catch { /* silent */ }
  }, [customerId]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (task) => {
    const status = task.status === 'done' ? 'open' : 'done';
    setTasks(ts => ts.map(t => t.id === task.id ? { ...t, status } : t));
    try { await axios.put(`${API}/tasks/${task.id}`, { status }); if (status === 'done') toast.success('Tarea completada'); }
    catch { toast.error('No se pudo actualizar'); load(); }
  };
  const add = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    try { await axios.post(`${API}/tasks`, { title: draft.trim(), customer_id: customerId, campaign_id: campaignId || null }); setDraft(''); await load(); }
    catch (e) { toast.error('No se pudo crear la tarea'); } finally { setBusy(false); }
  };

  const openCount = tasks.filter(t => t.status !== 'done').length;
  if (!customerId) return null;
  return (
    <div className="rounded-2xl border border-border bg-muted/20" data-testid="noah-tasks">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold" data-testid="noah-tasks-toggle">
        <ListTodo className="w-3.5 h-3.5 text-brand-green" /> Tareas y notas de Noah
        {openCount > 0 && <span className="rounded-full bg-brand-copper text-white text-[10px] px-1.5" data-testid="noah-tasks-open-count">{openCount}</span>}
        <ChevronDown className={cn('w-3.5 h-3.5 ml-auto transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          {tasks.length === 0 && notes.length === 0 && <p className="text-[11px] text-muted-foreground italic">Noah aún no ha creado tareas ni notas para este cliente.</p>}
          {tasks.map(t => (
            <button key={t.id} onClick={() => toggle(t)} className="w-full flex items-start gap-2 text-left text-sm py-1 touch-manipulation" data-testid={`task-${t.id}`}>
              {t.status === 'done' ? <CheckCircle2 className="w-4 h-4 text-brand-green shrink-0 mt-0.5" /> : <Circle className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />}
              <span className={cn('flex-1 min-w-0', t.status === 'done' && 'line-through text-muted-foreground')}>
                {t.title}{t.due_date && <span className="text-[10px] text-muted-foreground ml-1">· {t.due_date}</span>}
                <span className="text-[10px] text-muted-foreground ml-1">· {t.created_by === 'noah' ? 'Noah' : 'equipo'}</span>
              </span>
            </button>
          ))}
          {notes.map(n => (
            <div key={n.id} className="flex items-start gap-2 text-xs p-2 rounded-xl bg-brand-butter/30 text-brand-deep dark:text-brand-butter" data-testid={`note-${n.id}`}>
              <StickyNote className="w-3.5 h-3.5 shrink-0 mt-0.5" /><span className="flex-1">{n.value}</span>
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} placeholder="Nueva tarea para el equipo…" className="h-8 text-xs" data-testid="task-new-input" />
            <Button size="sm" variant="outline" onClick={add} disabled={busy || !draft.trim()} className="h-8" data-testid="task-new-btn">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}</Button>
          </div>
        </div>
      )}
    </div>
  );
};
